import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { BsModalService } from 'ngx-bootstrap/modal';
import { combineLatest, Observable, Subscription } from 'rxjs/index';
import { filter, map, tap } from 'rxjs/operators';
import { AuthenticationSelectors } from '../../auth/state/authentication.selectors';
import { Person } from '../../core/model/central/person.model';
import { Strategy } from '../../core/model/central/strategy.model';
import { User } from '../../core/model/user.model';
import { AccountService } from '../../core/services/account.service';
import { RouteSelectors } from '../../core/state/route/route.selectors';
import { PathAndParam } from '../../core/state/route/route.state';
import { GlobalSettingsActions } from '../reminder-settings/global-settings/state/global-settings.actions';
import { GlobalSettingsSelectors } from '../reminder-settings/global-settings/state/global-settings.selectors';
import { StrategyService } from '../reminder-settings/state/strategy.service';
import { PatientAppointmentService } from './state/patient-appointment.service';
import { PatientsActions } from './state/patients.actions';
import { PatientsSelectors } from './state/patients.selectors';
import { AppointmentListActions } from '../appointment-list/state/appointment-list.actions';

@Component({
    selector: 'patients',
    templateUrl: './patients.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class PatientsComponent implements OnDestroy, OnInit {
    currentUser$: Observable<User>;
    hasTwoWay$: Observable<boolean>;
    selectedPatient$: Observable<Person>;
    strategies$: Observable<Array<Strategy>>;
    patientStrategies$: Observable<Array<Strategy>>;
    selectedPatientStrategy$: Observable<Strategy>;
    loading$: Observable<boolean>;
    viewModel$: Observable<any>;
    appointmentsLoading$: Observable<boolean>;
    userRoute$: Observable<PathAndParam>;

    subscriptions: Subscription = new Subscription();

    constructor(private accountService: AccountService,
                private authenticationSelectors: AuthenticationSelectors,
                private patientsSelectors: PatientsSelectors,
                private strategyService: StrategyService,
                private globalSelector: GlobalSettingsSelectors,
                public router: Router,
                private activatedRoute: ActivatedRoute,
                private modalService: BsModalService,
                private patientAppointmentService: PatientAppointmentService,
                private store: Store<any>,
                private routeSelectors: RouteSelectors) {

        this.currentUser$ = this.authenticationSelectors.selectCurrentUser();
        this.hasTwoWay$ = this.currentUser$ // TODO: Refactor to composed selector
            .pipe(
                map(user =>
                    this.accountService.hasUserPermissionKey(user, 'twoway'))
            );
        this.subscriptions.add(
            this.currentUser$
            .pipe(
                filter(currentUser => currentUser !== undefined)
            )
            .subscribe(currentUser => {
                    this.store.dispatch(PatientsActions.getPatientSettings({userId: currentUser.userId}));
                }
            ));
        this.selectedPatient$ = this.patientsSelectors.selectSelectedPatient();
        this.appointmentsLoading$ = this.patientAppointmentService.loading$;

        this.userRoute$ = this.routeSelectors.selectRoutesPath()
          .pipe(map(routeData => routeData.USER));
    }

    ngOnInit(): void {
        this.parseQueryParams();

        this.setupLazyLoadingOfStrategies();

        this.setupStrategiesForReminderSettingsComponent();
        this.setupLoadingState();
        this.setupViewModel();
        this.store.dispatch(AppointmentListActions.setPageType({payload: 'PatientAppointment'}));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    selectPatient(personId: number, userQueryParam: string): void {
        // TODO: Move to side-effect?
        this.router.navigate(
            [],
            {
                relativeTo: this.activatedRoute,
                queryParams: { [userQueryParam]: personId },
                queryParamsHandling: 'merge'
            });
        // This will trigger parseQueryParams() below which will load selected patient
    }

    parseQueryParams(): void {
        // TODO: Move to higher level so selection will happen even if set on a different page?
        this.subscriptions.add(
            this.activatedRoute.queryParamMap.subscribe(params => {
            if (params.keys.findIndex(key => key === 'patientid') > -1) {
                const personId = +params.get('patientid');
                this.store.dispatch(PatientsActions.getSelectedPatient({personId}));
            }

            if (params.keys.findIndex(key => key === 'clientid') > -1) {
              const personId = +params.get('clientid');
              this.store.dispatch(PatientsActions.getSelectedPatient({personId}));
            }

            if (params.keys.findIndex(key => key === 'remotepatientid') > -1) {
                const remotePatientId = params.get('remotepatientid');
                this.store.dispatch(PatientsActions.getPatientByRemoteId({remotePatientId}));
            }
        }));
    }

    // Start loading already before patient selected so it's ready - by async subscribing early in the view
    setupLazyLoadingOfStrategies(): void {
        // Account strategy needed when adding "Global Strategy" entry for strategy dropdown
        this.store.dispatch(GlobalSettingsActions.getAccountSettingsLoggedInUser(undefined));

        this.strategies$ = this.strategyService.entities$
            .pipe(
                tap(strategies => {
                    if (strategies === undefined || strategies.length === 0) {
                        this.strategyService.getAll();
                    }
                }));
    }

    setupLoadingState(): void {
        this.loading$ = combineLatest([
            this.patientsSelectors.selectPatientSearchLoading(),
            this.patientsSelectors.selectPatientDetailsLoading(),
            this.appointmentsLoading$,
            this.patientsSelectors.selectMessagesLoading(),
            this.patientsSelectors.selectSetStrategyLoading()]
        )
            .pipe(
                map(([patientSearchLoading, patientDetailsLoading, appointmentsLoading,
                      messagesLoading, selectSetStrategyLoading]) =>
                    patientSearchLoading || patientDetailsLoading || appointmentsLoading || messagesLoading || selectSetStrategyLoading)
            );
    }

    setupStrategiesForReminderSettingsComponent(): void {
        this.patientStrategies$ = combineLatest([
            this.strategies$,
            this.globalSelector.selectStrategyId()])
            .pipe(
                map(([strategies, accountStrategyId]) =>
                    this.getStrategiesWithAccountStrategyFirst(strategies, accountStrategyId)
                )
            );

        this.selectedPatientStrategy$ = combineLatest([
            this.patientStrategies$,
            this.selectedPatient$,
            this.strategies$
        ])
            .pipe(

                map(([patientStrategies, selectedPatient, strategies]) => {
                    let patientStrategy = patientStrategies[0];
                    if (selectedPatient !== undefined) {
                        patientStrategy = selectedPatient.strategyId === null ? patientStrategies[0] : strategies.find(strategy => strategy.strategyId === selectedPatient.strategyId);
                    }

                    return patientStrategy;
                }));
    }

    // When we remove the refresh button and use WebSocket push we can remove viewModel
    setupViewModel(): void {
        this.viewModel$ = combineLatest([
            this.loading$,
            this.selectedPatient$]
        )
            .pipe(
                map(([loading, selectedPatient]) =>
                    ({
                        loading,
                        selectedPatient
                    }))
            );
    }

    getStrategiesWithAccountStrategyFirst(strategies, accountStrategyId): Array<Strategy> {
        if (strategies === undefined) {
            return strategies;
        }

        const accountStrategy = strategies.find(strategy => strategy.strategyId === accountStrategyId);
        const accountStrategyName =
            accountStrategy === undefined
                ? '--- Global strategy (NONE SELECTED) ---'
                : `--- Global strategy (${accountStrategy.strategyName}) ---`;

        accountStrategyId =
            accountStrategy === undefined
                ? 0
                : accountStrategyId;

        return [
            {
                strategyName: accountStrategyName,
                // tslint:disable-next-line:no-null-keyword
                strategyId: accountStrategyId
            },
            ...strategies];
    }

    setStrategy(strategy, patientId): void {
        // TODO: Move action to strategies? this requires that the actions, reducers and effects are in a module already loaded
        let strategyId: number = null;
        if (strategy.strategyName.indexOf('Global strategy') === -1) {
            strategyId = strategy.strategyId;
        }
        this.store.dispatch(PatientsActions.setPatientStrategy({patientId, strategyId, estimated: true}));
    }

    refreshPatient(personId): void {
        this.store.dispatch(PatientsActions.refresh());
        this.store.dispatch(PatientsActions.getSelectedPatient({personId}));
    }

    openSettings(): void {
        this.store.dispatch(PatientsActions.openPatientSettings());
    }
}
